import { fetchPythData } from '$lib/apis/pyth';
import { fetchingPythPrices, markets, pythPrices, stablesPrices } from '$lib/home/store';
import { marketsConfig } from '$lib/config/markets';
import type { Market } from '$lib/types/market';
import type { PythPrice, PythPriceResponse } from '$lib/types/pyth';
import { getMarketPrice, getNumBackInUnits, getPriceFromPythPrice, toBN } from '$lib/util/util';
import { get } from 'svelte/store';
import BigNumber from 'bignumber.js';
import type { IStablesPrices } from '$lib/web3/types';
import { EvmPriceServiceConnection } from '@pythnetwork/pyth-evm-js';
import {
	activeMarketsConfig,
	makePythQueryStringFromObject,
	marketsConfigBatches,
	pythFeedToMarket,
	pythFeedToMarketAsObject
} from './util';
import { get_pyth_url } from '$lib/apis/utils';

const PYTH_SYNCING_INTERVAL: number = 500;

let pyth: EvmPriceServiceConnection;
let connectionMonitorId: NodeJS.Timeout;
let lastConnectionTime: number = Date.now();
let pythFetchMethod: 'WS' | 'Polling' = 'WS';
let pythWSSyncingTimout: NodeJS.Timeout;
let pythPricesLocal: Record<string, PythPrice> = get(pythPrices);
let marketsLocal: Market[] = get(markets);

export function updateMarketCache(newMarkets: Market[]) {
	marketsLocal = newMarkets;
}

export async function setStablesToPyth(stablePrices: IStablesPrices[]) {
	if (pythPrices) {
		let marketPricesFromPyth: Record<string, any> = pythPrices;

		if (stablePrices) {
			stablePrices.forEach((value) => {
				if (value.symbol != 'USDT') {
					marketPricesFromPyth[value.symbol] = {
						price: value.price,
						decimals: 8,
						conf: '0',
						expo: -8,
						publish_time: Date.now()
					};
				}
			});
		}
		//Stables are not tracked by Pyth, so we need to add them manually
		marketPricesFromPyth['sUSD'] = {
			price: '100000000',
			decimals: 8,
			conf: '0',
			expo: -8,
			publish_time: Date.now()
		};

		pythPricesLocal = marketPricesFromPyth;
		pythPrices.set(marketPricesFromPyth);
	}
}

export function setPythData(response: Array<PythPriceResponse>, stablePrices: IStablesPrices[]) {
	let marketPricesFromPyth: Record<string, PythPrice> = {};
	if (Object.keys(activeMarketsConfig).length == response.length) {
		let marketNames = Object.keys(activeMarketsConfig);
		for (let i = 0; i < marketNames.length; i++) {
			marketPricesFromPyth[marketNames[i]] = response[i].price;
			marketPricesFromPyth[marketNames[i]].decimals =
				marketPricesFromPyth[marketNames[i]].expo * -1;
		}
	}

	if (stablePrices) {
		stablePrices.forEach((value) => {
			if (value.symbol != 'USDT') {
				marketPricesFromPyth[value.symbol] = {
					price: value.price,
					decimals: 8,
					conf: '0',
					expo: -8,
					publish_time: Date.now()
				};
			}
		});
	}
	//Stables are not tracked by Pyth, so we need to add them manually
	marketPricesFromPyth['sUSD'] = {
		price: '100000000',
		decimals: 8,
		conf: '0',
		expo: -8,
		publish_time: Date.now()
	};

	pythPricesLocal = marketPricesFromPyth;
	pythPrices.set(marketPricesFromPyth);
	fetchingPythPrices.set(false);

	//set current market oracle price as well
	if (get(markets)) {
		let updatedMarkets: Array<Market> = [];
		get(markets).forEach((m) => {
			if (marketPricesFromPyth.hasOwnProperty(m.asset)) {
				m.currentOraclePrice = getPriceFromPythPrice(marketPricesFromPyth[m.asset]);
				m.currentMarketPrice = getMarketPrice(m);
				m.maxMarketValueUSD = getNumBackInUnits(
					new BigNumber(m.maxMarketValue).times(m.currentMarketPrice).div(1e18).toFixed(0),
					18,
					2
				);
				m.shortOIUSD = getNumBackInUnits(
					new BigNumber(m.shortOI).times(m.currentMarketPrice).div(1e18).toFixed(0),
					18,
					2
				);
				m.longOIUSD = getNumBackInUnits(
					new BigNumber(m.longOI).times(m.currentMarketPrice).div(1e18).toFixed(0),
					18,
					2
				);
				const change24h = new BigNumber(m.currentMarketPrice).minus(new BigNumber(m?.price24hAgo));
				const change24hPercentage = change24h
					.div(new BigNumber(m.currentMarketPrice))
					.multipliedBy(100)
					.toFixed(2);
				m.change24hPercentage = change24hPercentage;
				m.change24h = getNumBackInUnits(
					change24h.toFixed(8),
					18,
					marketsConfig[m?.asset].showDecimals
				);
				updatedMarkets.push(m);
			}
		});
		marketsLocal = updatedMarkets;
		markets.set(updatedMarkets);
	}
}

export async function getPythData() {
	try {
		fetchingPythPrices.set(true);
		const queryArray = marketsConfigBatches.map((batch) => makePythQueryStringFromObject(batch));
		const responseArray = await Promise.all(queryArray.map((query) => fetchPythData(query)));
		const response: Array<PythPriceResponse> = responseArray.flat() as Array<PythPriceResponse>;
		setPythData(response, get(stablesPrices));
	} catch (e) {
		fetchingPythPrices.set(false);
	}
}

const subscribePriceFeeds = (connection: EvmPriceServiceConnection, priceIds: `0x${string}`[]) => {
	if (pythWSSyncingTimout) clearInterval(pythWSSyncingTimout);
	pythWSSyncingTimout = setInterval(() => {
		pythPrices.set(pythPricesLocal);
		markets.set(marketsLocal);
	}, PYTH_SYNCING_INTERVAL);

	connection.subscribePriceFeedUpdates(priceIds, (priceFeed) => {
		const priceResponse = {
			price: {
				conf: priceFeed.getPriceUnchecked()?.conf,
				expo: priceFeed.getPriceUnchecked()?.expo,
				price: priceFeed.getPriceUnchecked()?.price,
				publish_time: priceFeed.getPriceUnchecked()?.publishTime,
				decimals: -1 * priceFeed.getPriceUnchecked()?.expo
			}
		};

		pythFeedToMarket[`0x${priceFeed.id}`].forEach((market) => {
			pythPricesLocal[market] = priceResponse.price;
		});

		marketsLocal.forEach((market) => {
			if (pythFeedToMarketAsObject[`0x${priceFeed.id}`][market.asset]) {
				market.currentOraclePrice = getPriceFromPythPrice(priceResponse.price);
				market.currentMarketPrice = getMarketPrice(market);
				market.maxMarketValueUSD = getNumBackInUnits(
					new BigNumber(market.maxMarketValue)
						.times(market.currentMarketPrice)
						.div(1e18)
						.toFixed(0),
					18,
					2
				);
				market.shortOIUSD = getNumBackInUnits(
					new BigNumber(market.shortOI).times(market.currentMarketPrice).div(1e18).toFixed(0),
					18,
					2
				);
				market.longOIUSD = getNumBackInUnits(
					new BigNumber(market.longOI).times(market.currentMarketPrice).div(1e18).toFixed(),
					18,
					2
				);
				const change24h = new BigNumber(market.currentMarketPrice).minus(
					new BigNumber(market?.price24hAgo)
				);
				const change24hPercentage = change24h
					.div(new BigNumber(market.currentMarketPrice))
					.multipliedBy(100)
					.toFixed(2);
				market.change24hPercentage = change24hPercentage;
				market.change24h = getNumBackInUnits(
					change24h.toFixed(8),
					18,
					marketsConfig[market?.asset].showDecimals
				);
			}
		});

		lastConnectionTime = Date.now();
	});
};

export async function connectToPyth() {
	if (pyth) {
		pyth.closeWebSocket();
	}

	const PYTH_URL = get_pyth_url();

	pyth = new EvmPriceServiceConnection(PYTH_URL, {});

	pyth.onWsError = (error: Error) => {
		if (error) {
			console.log(error);
		}
	};

	const priceIdsMainnet: `0x${string}`[] = Object.values(activeMarketsConfig).map(
		(market) => market.pythFeedKey
	);

	subscribePriceFeeds(pyth, priceIdsMainnet);
}

const polling = async () => {
	try {
		const queryArray = marketsConfigBatches.map((batch) => makePythQueryStringFromObject(batch));
		const responseArray = await Promise.all(queryArray.map((query) => fetchPythData(query)));
		const response: Array<PythPriceResponse> = responseArray.flat() as Array<PythPriceResponse>;
		if (response) {
			setPythData(response, get(stablesPrices));
			lastConnectionTime = Date.now();
		}
	} catch (err) {
		console.log(err);
	}
};

const setupPolling = () => {
	setInterval(() => {
		polling();
	}, 1000);
};

const switchConnection = () => {
	pythFetchMethod = pythFetchMethod === 'WS' ? 'Polling' : 'WS';
	if (pythFetchMethod === 'WS') {
		connectToPyth();
	} else {
		if (pyth) {
			pyth.closeWebSocket();
		}
		if (pythWSSyncingTimout) clearInterval(pythWSSyncingTimout);
		setupPolling();
	}
};

const monitorConnection = () => {
	if (connectionMonitorId) clearTimeout(connectionMonitorId);
	connectionMonitorId = setTimeout(() => {
		if (Date.now() - lastConnectionTime > 10000) {
			switchConnection();
		}
		monitorConnection();
	}, 1000);
};
export async function setupPythData() {
	try {
		fetchingPythPrices.set(true);
		// make first request via api
		const queryArray = marketsConfigBatches.map((batch) => makePythQueryStringFromObject(batch));
		const responseArray = await Promise.all(queryArray.map((query) => fetchPythData(query)));
		const response: Array<PythPriceResponse> = responseArray.flat() as Array<PythPriceResponse>;
		setPythData(response, get(stablesPrices));
		lastConnectionTime = Date.now();

		monitorConnection();
		connectToPyth();
	} catch (err) {
		fetchingPythPrices.set(false);
		console.log(err);
	}
}
